import fontColorContrast from "font-color-contrast";
import { Fragment, useCallback, useState } from "react";
import { useHistory } from "react-router";
import { common_utils } from "../../../../../../common/common_utils";
import { Constants } from "../../../../../../common/constants";
import { DashboardUtils } from "../../../dashboard_utils";
import { trpc } from "../../../utils/trpc";
import { BackButton } from "../base_components/back_button";
import { DashboardContainer } from "../base_components/dashboard_container";
import { ModifySettingsButton } from "../base_components/modify_settings_button";
import { SeshPrimaryButton } from "../base_components/sesh_primary_button";
import { SeshSearchForm } from "../base_components/sesh_search_form";
import { useEventCreationContext } from "../hooks/use_event_creation_context";
import { useIsAdmin } from "../hooks/use_is_admin";
import { useServerId } from "../hooks/use_server_id";
import { useTemplateId } from "../hooks/use_template_id";
import { Note } from "../note";
import { InfoTooltip } from "../tooltips/info_tooltip";
import { WithTooltipPortaled } from "../tooltips/with_tooltip_portaled";
import { EventTemplateBookmarkedList } from "./event_template_bookmark_list";
import { PlaceholderRow } from "./event_template_list";
import { EventTemplateListTab } from "./event_template_list_tab";
import {
	ExistingTemplateViewer,
	StateTemplateViewer,
} from "./event_template_modal_info_page";
import { EventTemplatePrevEventList } from "./event_template_prev_event_list";

export function EventTemplateDropdown(props: {}) {
	const event_context = useEventCreationContext();

	const template_name = event_context.props.template_data?.name;

	return (
		<>
			<div className="flex w-full">
				<div className="ml-auto flex items-center gap-2">
					<button
						className="flex items-center gap-2 font-bold text-sesh-primary hover:text-sesh-dark"
						onClick={() =>
							event_context.setState({
								current_page: "templates",
							})
						}
					>
						{template_name ?? "Template"}{" "}
						<i className="fas fa-pencil-alt"></i>
					</button>
				</div>
			</div>
		</>
	);
}

export type EventTemplateListView = "Bookmarks" | "Previous Events";

export function EventTemplatesHeader() {
	return (
		<div className="flex items-center gap-2">
			<div>Event Templates</div>
			<span className="flex items-end text-2xl">
				<InfoTooltip
					info={
						<>
							Event templates allow server members to quickly
							create new events using the settings from previous
							events. Admins are also able to manage template
							favorites for quick selection for anyone in the
							server. For more info, see{" "}
							<a
								className="manual-link"
								href="/manual/#event_templates"
							>
								here
							</a>
							.
						</>
					}
				></InfoTooltip>
			</span>
		</div>
	);
}

export function EventTemplatePage(props: {
	onBack?: () => void;
	is_guild_premium: boolean;
}) {
	const isAdmin = useIsAdmin();

	return (
		<DashboardContainer
			title={<EventTemplatesHeader />}
			bread_crumbs={
				props.onBack && (
					<BackButton label="Back" onClick={props.onBack} />
				)
			}
			is_loading={!isAdmin.isSuccess}
		>
			<EventTemplateContents is_guild_premium={props.is_guild_premium} />
		</DashboardContainer>
	);
}

export function EventTemplateContents(props: {
	is_from_guild_settings?: boolean;
	is_guild_premium: boolean;
}) {
	const server_id = useServerId();

	const [selected_info_template, setSelectedInfoId] = useState(
		null as null | TemplateSelection
	);
	const [selected_template, setSelectedId] = useState(
		null as null | TemplateSelection
	);

	const event_list_query = trpc.event_template.getBookmarkedList.useQuery({
		guild_id: server_id,
	});

	const [search, setSearch] = useState("");

	const history = useHistory();

	const is_viewing_current_settings =
		selected_info_template?.message_id === "CURRENT";

	let bookmark_count = event_list_query.data?.length;

	const are_server_bookmarks_full =
		bookmark_count >=
		Constants.MAXIMUM_EVENT_TEMPLATES[
			props.is_guild_premium ? "premium" : "free"
		];

	const [is_favorites_collapsed, setFavoritesCollapsed] = useState(false);
	const [is_previous_events_collapsed, setPreviousEventsCollapsed] =
		useState(false);

	const onUseClick = useCallback(
		(template: TemplateSelection) => {
			const type = template.template_id ? "template" : "event";

			const selected_template_id =
				type === "template"
					? template.template_id
					: template.message_id;

			history.push(
				common_utils.getEventCreateURL(server_id, undefined, {
					type,
					id: selected_template_id,
				}),
				{
					uuid: Math.random(),
				}
			);
		},
		[history]
	);

	return (
		<>
			<div className="mx-auto flex w-11/12 flex-col sm:w-3/4">
				<div className="flex flex-col gap-2">
					<div className="ml-auto w-64">
						<SeshSearchForm
							onSubmit={setSearch}
							placeholder="Search by name"
						/>
					</div>
				</div>
				<div className="h-[512px] overflow-auto">
					<ListHeader
						label={
							<div className="flex items-center gap-2">
								<div>Favorited Templates</div>
								{!props.is_guild_premium && (
									<>
										<div>
											({bookmark_count}/
											{
												Constants
													.MAXIMUM_EVENT_TEMPLATES[
													"free"
												]
											}
											)
										</div>
										<WithTooltipPortaled
											style="Info"
											tooltip={
												"Server favorites are limited to 3 per server. Upgrade this server to premium to have unlimited favorites."
											}
										>
											<i className="fa fa-info-circle fa-sm"></i>
										</WithTooltipPortaled>
									</>
								)}
							</div>
						}
						is_collapsed={is_favorites_collapsed}
						setCollapsed={setFavoritesCollapsed}
					/>
					{!is_favorites_collapsed && (
						<EventTemplateBookmarkedList
							event_list_query={event_list_query}
							setSelectedTemplate={setSelectedId}
							setSelectedInfoTemplate={setSelectedInfoId}
							onUseClick={onUseClick}
							selected_template={selected_template}
							search={search}
						/>
					)}
					<ListHeader
						label={"Previous Events"}
						is_collapsed={is_previous_events_collapsed}
						setCollapsed={setPreviousEventsCollapsed}
					/>
					{!is_previous_events_collapsed && (
						<EventTemplatePrevEventList
							setSelectedTemplate={setSelectedId}
							setSelectedInfoTemplate={setSelectedInfoId}
							onUseClick={onUseClick}
							selected_template={selected_template}
							are_bookmarks_full={are_server_bookmarks_full}
							search={search}
						/>
					)}
				</div>
			</div>
			{selected_info_template && (
				<DashboardUtils.SeshModal
					title={selected_info_template.name + " Template"}
					content={
						<div className="mt-2">
							{is_viewing_current_settings ? (
								<StateTemplateViewer
									onClose={() => setSelectedInfoId(null)}
								/>
							) : (
								<ExistingTemplateViewer
									selected_template={selected_info_template}
								/>
							)}
						</div>
					}
					is_active
					onClose={() => setSelectedInfoId(null)}
				/>
			)}
		</>
	);
}

export function SaveCurrentSettingsButton(props: { is_premium: boolean }) {
	const [is_modal_opened, setIsModalOpened] = useState(false);

	const isAdmin = useIsAdmin();
	const server_id = useServerId();

	const event_list_query = trpc.event_template.getBookmarkedList.useQuery({
		guild_id: server_id,
	});

	if (isAdmin.isLoading || event_list_query.isLoading) {
		return <></>;
	}

	const are_server_bookmarks_full =
		event_list_query.data.length >=
		Constants.MAXIMUM_EVENT_TEMPLATES[
			props.is_premium ? "premium" : "free"
		];

	let tooltip: string = "";
	if (are_server_bookmarks_full) {
		tooltip = "Favorites full";
	} else if (!isAdmin.data) {
		tooltip = "Requires Manage sesh permissions";
	}

	return (
		<>
			<div className="w-fit">
				<WithTooltipPortaled style="Info" tooltip={tooltip}>
					<ModifySettingsButton
						className="whitespace-nowrap"
						hide_arrow
						disabled={!isAdmin.data || are_server_bookmarks_full}
						onClick={() => setIsModalOpened(true)}
					>
						Save Current Settings as Template
						{are_server_bookmarks_full ? " (Favorites Full)" : ""}
					</ModifySettingsButton>
				</WithTooltipPortaled>
			</div>
			{is_modal_opened && (
				<DashboardUtils.SeshModal
					title={"Current Settings Template"}
					content={
						<div className="mt-2">
							<StateTemplateViewer
								onClose={() => setIsModalOpened(false)}
							/>
						</div>
					}
					is_active
					onClose={() => setIsModalOpened(false)}
				/>
			)}
		</>
	);
}

export type TemplateSelection = {
	message_id?: string;
	template_id?: string;
	name: string;
};

function ListHeader(props: {
	label: string | JSX.Element;
	setCollapsed: (val: boolean) => void;
	is_collapsed: boolean;
}) {
	const { setCollapsed, is_collapsed } = props;

	return (
		<div className="sticky top-0 z-[1]">
			<button
				className="group w-full"
				onClick={() => setCollapsed(!is_collapsed)}
			>
				<PlaceholderRow>
					<div className="flex w-full flex-col items-center px-4 transition-all group-hover:text-sesh-primary">
						<span className="font-bold">{props.label}</span>

						<div
							className="absolute right-4"
							key={is_collapsed ? "collapsed" : "not_collapsed"}
						>
							{is_collapsed ? (
								<i className="fas fa-chevron-down"></i>
							) : (
								<i className="fas fa-chevron-up"></i>
							)}
						</div>
					</div>
				</PlaceholderRow>
			</button>
		</div>
	);
}

export function ColoredColor(props: { value: string }) {
	const color_contrast = fontColorContrast(props.value);

	return (
		<div
			className="w-fit rounded-lg border px-2 text-sm"
			style={{
				backgroundColor: props.value,
				borderColor:
					color_contrast === "#000000" ? "transparent" : "#00f4ff",
				color: color_contrast,
			}}
		>
			{props.value}
		</div>
	);
}

export type EventTemplateModalSelectedStateRowProps =
	| {
			label: string;
			value: React.ReactNode;
	  }
	| {
			label: string;
			childs: EventTemplateModalSelectedStateRowProps[];
	  };
